import HomeIconInactive from "../images/icons/homeIconInactive.svg?react";
import HomeIconActive from "../images/icons/homeIconActive.svg?react";
import ExploreIconActive from "../images/icons/exploreIconActive.svg?react";
import ExploreIconInactive from "../images/icons/exploreIconInactive.svg?react";
import PlusIcon from "../images/icons/plusThink.svg?react";
import PlusThinkActive from "../images/icons/plusThinkActive.svg?react";
import ProfileIconActive from "../images/icons/profileIconActive.svg?react";
import ProfileIconInactive from "../images/icons/profileIconInactive.svg?react";
import NicheIcon from "../images/icons/niche.svg?react";
import NicheIconActive from "../images/icons/niche-filled.svg?react";
import { useMemo } from "react";
import { ExploreType, routes } from "@/common/utils/routes";
import { TABS_LABELS, TABS_URLS } from "../helpers";
import { useExitSnapFeed } from "./useExitSnapFeed";
import { Link, useLocation } from "react-router-dom";
import { MenuButton, mainBus } from "@/common/buses";
import { useAppStore } from "@/common/store/appStore";
import { FeedSection, IFeedVisit, useFeedResumeStore } from "@/common/store/feedResumeStore";
import { FeedNavigation, getFeedNavigation } from "@/common/utils/feedResume";

type IsTabActive = (tab: string) => string | boolean | null;

type MenuIcon = typeof HomeIconActive;

interface IFeedMenuItemConfig {
  section: FeedSection;
  visit?: IFeedVisit;
  defaultPath: string;
  tab: TABS_URLS;
  label: TABS_LABELS;
  button: MenuButton;
  icons: { active: MenuIcon; inactive: MenuIcon };
  onStartOver?: () => void;
}

const handleFeedTap = (navigation: FeedNavigation, onStartOver?: () => void) => {
  if (navigation.kind === "closePlayer") {
    mainBus.emit("toggle_fullscreen");
  }

  if (navigation.kind === "startOver") {
    onStartOver?.();
  }
};

interface UseFooterMenuItemsParams {
  currentUserName: string | null;
  isTabActive: IsTabActive;
  onSendMetricAndCloseMenu: (
    tabName: TABS_URLS,
    label: TABS_LABELS,
    button: MenuButton,
  ) => void;
  onProfileClick: () => void;
  isProfileMenuOpen: boolean;
  isVerified: boolean;
}

export const useFooterMenuItems = ({
  currentUserName,
  isTabActive,
  onSendMetricAndCloseMenu,
  onProfileClick,
  isProfileMenuOpen,
  isVerified,
}: UseFooterMenuItemsParams) => {
  const exitSnapFeed = useExitSnapFeed();
  const homeVisit = useFeedResumeStore((state) => state.visits[FeedSection.home]);
  const exploreVisit = useFeedResumeStore((state) => state.visits[FeedSection.explore]);
  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const { pathname, search } = useLocation();

  const isTabActiveFor = (tab: TABS_URLS): boolean =>
    !isProfileMenuOpen && !!isTabActive(tab);

  const menuItems = useMemo(() => {
    const createFeedMenuItem = ({ section, visit, defaultPath, tab, label, button, icons, onStartOver }: IFeedMenuItemConfig) => {
      const navigation = getFeedNavigation({ section, location: { pathname, search }, visit, defaultPath, isPlayerOpen: isFullScreen });
      const isHighlighted = isTabActiveFor(tab);

      return {
        linkComponent: Link,
        linkComponentProp: "to",
        linkComponentProps: navigation.linkProps,
        icon: isHighlighted ? icons.active : icons.inactive,
        link: navigation.link,
        title: label,
        onClick: () => {
          handleFeedTap(navigation, onStartOver);
          onSendMetricAndCloseMenu(tab, label, button);
        },
        isActive: isHighlighted,
      };
    };

    return [
      createFeedMenuItem({
        section: FeedSection.home,
        visit: homeVisit,
        defaultPath: routes.home(),
        tab: TABS_URLS.home,
        label: TABS_LABELS.home,
        button: "home",
        icons: { active: HomeIconActive, inactive: HomeIconInactive },
        onStartOver: exitSnapFeed,
      }),
      createFeedMenuItem({
        section: FeedSection.explore,
        visit: exploreVisit,
        defaultPath: routes.explore(ExploreType.gifs),
        tab: TABS_URLS.explore,
        label: TABS_LABELS.explore,
        button: "explore",
        icons: { active: ExploreIconActive, inactive: ExploreIconInactive },
      }),
      {
        linkComponent: Link,
        linkComponentProp: "to",
        icon: isTabActiveFor(TABS_URLS.add) ? PlusThinkActive : PlusIcon,
        link: routes.create(),
        title: isVerified ? TABS_LABELS.studio : TABS_LABELS.add,
        onClick: () => onSendMetricAndCloseMenu(
          TABS_URLS.add,
          isVerified ? TABS_LABELS.studio : TABS_LABELS.add,
          isVerified ? "studio" : "upload",
        ),
        isActive: isTabActiveFor(TABS_URLS.add)
      },
      {
        linkComponent: Link,
        linkComponentProp: "to",
        icon: isTabActiveFor(TABS_URLS.niches) ? NicheIconActive : NicheIcon,
        link: routes.niches(),
        title: TABS_LABELS.niches,
        onClick: () => onSendMetricAndCloseMenu(
          TABS_URLS.niches,
          TABS_LABELS.niches,
          "niches",
        ),
        isActive: isTabActiveFor(TABS_URLS.niches)
      },
      {
        linkComponent: Link,
        linkComponentProp: "to",
        icon: isTabActive(TABS_URLS.profile) || isProfileMenuOpen ? ProfileIconActive : ProfileIconInactive,
        title: currentUserName ? TABS_LABELS.profile : TABS_LABELS.login,
        onClick: onProfileClick,
        isActive: !!(isTabActive(TABS_URLS.profile) || isProfileMenuOpen)
      },
    ];
  }, [
    currentUserName,
    exitSnapFeed,
    exploreVisit,
    homeVisit,
    isFullScreen,
    isProfileMenuOpen,
    isTabActive,
    isVerified,
    onSendMetricAndCloseMenu,
    onProfileClick,
    pathname,
    search,
  ]);

  return menuItems;
};
